/**
 * Workload — ADMIN track.
 *
 * Per-assignee distribution so the admin can see where work is piling up.
 * Admin-gated route + a `visibilityFor` re-check (seesCrossProject).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  BUILD B (2026-05-21) — real task load.
 * ─────────────────────────────────────────────────────────────────────────
 *  The task dimension was previously demo data: `adminAggregate().workload`
 *  surfaced per-assignee OPEN-PROPOSAL counts from `trackDemo` (a Sprint-3
 *  demo scaffold). It now reads the REAL top-level `tasks/` collection — the
 *  same records the AI assistant (in-app + WhatsApp) creates.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  BUG 2 (2026-05-22) — anchor on the real staff roster.
 * ─────────────────────────────────────────────────────────────────────────
 *  The screen had built its people list purely from `groupByAssignee` over
 *  the live `tasks/` docs — so it showed ONLY whoever owned a task, never
 *  the full staff roster, and surfaced loose non-staff-id assignee strings
 *  as phantom people. It now reads the SAME staff roster `SettingsStaffRoles`
 *  uses (`listenAvbpStaff`) and runs `rosterWorkload`: every ACTIVE staff
 *  member is a row (0 if they own no tasks); loose strings resolve to a real
 *  staffer where possible, else group under a clear "unmatched" bucket;
 *  `Unassigned` stays its own bucket. The detailed per-task breakdown is the
 *  All Tasks screen.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, Card, EmptyNote, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import { useToast } from '../components/Toast';
import {
  listenTasks,
  rosterWorkload,
  openCount,
  type LiveTask,
} from '../lib/tasksStore';
import { listenAvbpStaff, type AvbpStaffRow } from '../lib/staffRoles';

export default function Workload() {
  const { role } = useAuth();
  const { toast } = useToast();
  const [tasks, setTasks] = useState<LiveTask[]>([]);
  const [staff, setStaff] = useState<AvbpStaffRow[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [staffLoading, setStaffLoading] = useState(true);

  useEffect(() => {
    const unsub = listenTasks(setTasks, {
      onErr: () => toast('Could not load task workload (check connection)'),
      onReady: () => setTasksLoading(false),
    });
    return unsub;
  }, [toast]);

  // BUG 2 — the real staff roster, the SAME source SettingsStaffRoles uses.
  // listenAvbpStaff has no onReady; the first callback (success or error)
  // flips staffLoading false so the load gate is honest.
  useEffect(() => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        setStaffLoading(false);
      }
    };
    const unsub = listenAvbpStaff(
      (rows) => {
        setStaff(rows);
        finish();
      },
      () => {
        toast('Could not load the staff roster (check connection)');
        finish();
      },
    );
    return unsub;
  }, [toast]);

  if (!visibilityFor(role).seesCrossProject) {
    return (
      <Box title="Workload" note="Restricted">
        <EmptyNote title="Not available on this track">
          The cross-project workload view is admin-only.
        </EmptyNote>
      </Box>
    );
  }

  const loading = tasksLoading || staffLoading;

  // Roster-anchored: every active staffer is a row (0 if no tasks); loose
  // assignee strings resolve to real staff where possible; Unassigned is its
  // own bucket. The bar is scaled to the heaviest row.
  const rows = rosterWorkload(tasks, staff);
  const maxLoad = Math.max(1, ...rows.map((r) => r.openCount));
  const totalOpen = openCount(tasks);

  return (
    <Box
      title="Workload by assignee"
      note={`Open tasks per staff member — ${totalOpen} open across the team (live tasks/)`}
    >
      {loading && <ListLoading label="Loading workload…" rows={3} />}

      {!loading && rows.length === 0 && (
        <EmptyNote title="No staff roster found">
          No AVBP staff members were found, and no tasks have been created.
          Add staff in Settings → Staff roles.
        </EmptyNote>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {rows.map((r) => (
              <Card key={r.key} flat>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <b>{r.name}</b>
                  {r.kind === 'unassigned' && (
                    <Tag kind="warn">unassigned</Tag>
                  )}
                  {r.kind === 'unmatched' && (
                    <Tag
                      kind="warn"
                      title="This assignee string could not be matched to a staff member — likely a typo or a person not on the AVBP roster."
                    >
                      not on roster
                    </Tag>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    {r.openCount} open ·{' '}
                    {r.tasks.length} task{r.tasks.length === 1 ? '' : 's'} total
                  </span>
                </div>
                <div
                  style={{
                    height: 8,
                    background: 'var(--inset-deep)',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${(r.openCount / maxLoad) * 100}%`,
                      height: '100%',
                      background:
                        r.openCount > 4 ? 'var(--crit)' : 'var(--good)',
                    }}
                  />
                </div>
              </Card>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <Link to="/all-tasks">
              <Btn3 intent="ghost" size="sm">
                Open All Tasks — per-task breakdown
              </Btn3>
            </Link>
          </div>
        </>
      )}
    </Box>
  );
}
